import { useQuery } from "@tanstack/react-query";
import { MarketTag } from "../components/MarketTag";
import { useState } from "react";
import type { ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ImportPanel, useUserPrefs } from "@strat/ui";

import { api } from "../api";
import { SortableTable } from "../components/SortableTable";
import { Stars } from "../components/social";
import { dd, money, pct, timeAgo, tone } from "../format";
import { LAYOUT_NAME } from "../nav";
import { useSession } from "../session";
import { Avatar } from "../shell/Avatar";
import { Guard } from "../shell/Guard";
import { MySpaceTabs } from "../shell/MySpaceTabs";
import { Card, EmptyState } from "../shell/ui";
import { BotState } from "./social/bots";
import { DASHBOARD_CARDS, arrangeCards, moveCard, setCardVisible } from "../components/dashboardCards";
import type { DashboardCardId } from "../components/dashboardCards";
import { directionColumn } from "../components/direction";

interface Space {
  strategies: Array<{
    id: string;
    name: string;
    visibility: string;
    latest_version: number;
    rating_avg: number;
    rating_count: number;
    comment_count: number;
    updated_at: string;
  }>;
  runs: Array<{
    id: string;
    strategy_name: string;
    symbol: string;
    timeframe: string;
    market_type?: string;
    leverage?: number;
    direction?: "long" | "short";
    total_return_pct: number;
    max_drawdown_pct: number;
    total_trades: number;
    visibility: string;
    created_at: string;
  }>;
  bots: Array<{
    id: string;
    name: string;
    symbol: string;
    timeframe: string;
    market_type?: string;
    leverage?: number;
    direction?: "long" | "short";
    status: string;
    desired_state: string;
    return_pct: number | null;
    max_drawdown_pct: number | null;
    equity: number | null;
    trade_count: number;
  }>;
  followers: number;
}

function MySpaceInner() {
  const { user, layout } = useSession();
  const navigate = useNavigate();
  const q = useQuery({
    queryKey: ["space"],
    queryFn: () => api<Space>("/hub/me/space"),
    refetchInterval: 30_000,
  });
  const { prefs, update } = useUserPrefs();
  const [customizing, setCustomizing] = useState(false);
  const [importing, setImporting] = useState(false);
  if (!user) return null;
  const s = q.data;
  const arranged = arrangeCards(prefs.dashboard.cards);
  const cards: Record<DashboardCardId, ReactNode> = {
    strategies: (
      <Card
        title="My strategies"
        actions={
          <Link className="btn btn--sm btn--ghost" to="/strategies">
            All
          </Link>
        }
        flush
      >
        <SortableTable
          exportName={null}
          rows={s?.strategies ?? []}
          pageSize={10}
          loading={q.isPending}
          rowKey={(r) => r.id}
          onRowClick={(r) => navigate(`/s/${r.id}`)}
          empty={
            <EmptyState
              icon="◇"
              title="No strategies yet"
              action={
                <Link className="btn btn--primary btn--sm" to="/workbench">
                  Open the Workbench
                </Link>
              }
            >
              Build one, or fork something from Explore.
            </EmptyState>
          }
          columns={[
            {
              key: "name",
              label: "Strategy",
              render: (r) => <strong>{r.name}</strong>,
              sortValue: (r) => r.name,
              firstDirection: "asc",
            },
            {
              key: "vis",
              label: "Status",
              render: (r) =>
                r.visibility === "private" ? (
                  <span className="muted">draft</span>
                ) : (
                  <span className="badge badge--success">
                    v{r.latest_version} {r.visibility}
                  </span>
                ),
              sortValue: (r) => r.visibility,
            },
            {
              key: "rating",
              label: "Rating",
              render: (r) => <Stars value={r.rating_avg} count={r.rating_count} size="sm" />,
              sortValue: (r) => (r.rating_count ? r.rating_avg : null),
            },
            {
              key: "comments",
              label: "Comments",
              align: "right",
              render: (r) => r.comment_count,
              sortValue: (r) => r.comment_count,
            },
            {
              key: "updated",
              label: "Updated",
              align: "right",
              render: (r) => timeAgo(r.updated_at),
              sortValue: (r) => new Date(r.updated_at).getTime(),
            },
          ]}
        />
      </Card>
    ),
    backtests: (
      <Card
        title="Recent backtests"
        actions={
          <Link className="btn btn--sm btn--ghost" to="/history">
            All
          </Link>
        }
        flush
      >
        <SortableTable
          exportName={null}
          rows={s?.runs ?? []}
          pageSize={10}
          loading={q.isPending}
          rowKey={(r) => r.id}
          onRowClick={(r) => navigate(`/r/${r.id}`)}
          empty={
            <EmptyState icon="▦" title="No backtests yet">
              Every backtest you save lands here.
            </EmptyState>
          }
          columns={[
            {
              key: "name",
              label: "Strategy",
              render: (r) => <strong>{r.strategy_name}</strong>,
              sortValue: (r) => r.strategy_name,
              firstDirection: "asc",
            },
            {
              key: "market",
              label: "Market",
              render: (r) => (
                <span className="mono small">
                  {r.symbol} {r.timeframe} <MarketTag marketType={r.market_type} leverage={r.leverage} />
                </span>
              ),
              sortValue: (r) => r.symbol,
              firstDirection: "asc",
            },
            directionColumn(),
            {
              key: "ret",
              label: "Return",
              align: "right",
              render: (r) => <span className={tone(r.total_return_pct)}>{pct(r.total_return_pct)}</span>,
              sortValue: (r) => r.total_return_pct,
            },
            {
              key: "dd",
              label: "Max DD",
              align: "right",
              render: (r) => dd(r.max_drawdown_pct, 1),
              sortValue: (r) => r.max_drawdown_pct,
              firstDirection: "asc",
            },
            {
              key: "trades",
              label: "Trades",
              align: "right",
              render: (r) => r.total_trades,
              sortValue: (r) => r.total_trades,
            },
            {
              key: "pub",
              label: "Status",
              render: (r) =>
                r.visibility === "public" ? (
                  <span className="badge badge--success">published</span>
                ) : (
                  <span className="muted">private</span>
                ),
              sortValue: (r) => r.visibility,
            },
            {
              key: "when",
              label: "Run",
              align: "right",
              render: (r) => timeAgo(r.created_at),
              sortValue: (r) => new Date(r.created_at).getTime(),
            },
          ]}
        />
      </Card>
    ),
    bots: (
      <Card
        title={layout === "starter" ? "My bot" : "My bots"}
        actions={
          <Link className="btn btn--sm btn--ghost" to="/bots">
            All
          </Link>
        }
        flush
      >
        <SortableTable
          exportName={null}
          rows={s?.bots ?? []}
          pageSize={10}
          loading={q.isPending}
          rowKey={(r) => r.id}
          onRowClick={(r) => navigate(`/b/${r.id}`)}
          empty={
            <EmptyState icon="◎" title="No paper bots running">
              Open a strategy and press “Run as paper bot”.
            </EmptyState>
          }
          columns={[
            {
              key: "name",
              label: "Bot",
              render: (r) => <strong>{r.name}</strong>,
              sortValue: (r) => r.name,
              firstDirection: "asc",
            },
            {
              key: "market",
              label: "Market",
              render: (r) => (
                <span className="mono small">
                  {r.symbol} {r.timeframe} <MarketTag marketType={r.market_type} leverage={r.leverage} />
                </span>
              ),
              sortValue: (r) => `${r.symbol} ${r.timeframe}`,
              firstDirection: "asc",
            },
            directionColumn(),
            {
              key: "state",
              label: "State",
              render: (r) => <BotState status={r.status} desired={r.desired_state} />,
              sortValue: (r) => r.status,
            },
            {
              key: "ret",
              label: "Return",
              align: "right",
              render: (r) => <span className={tone(r.return_pct)}>{pct(r.return_pct, 2)}</span>,
              sortValue: (r) => r.return_pct,
            },
            {
              key: "dd",
              label: "Max DD",
              align: "right",
              render: (r) => dd(r.max_drawdown_pct),
              sortValue: (r) => r.max_drawdown_pct,
              firstDirection: "asc",
            },
            {
              key: "equity",
              label: "Equity",
              align: "right",
              render: (r) => money(r.equity, 2),
              sortValue: (r) => r.equity,
            },
            {
              key: "trades",
              label: "Trades",
              align: "right",
              render: (r) => r.trade_count,
              sortValue: (r) => r.trade_count,
            },
          ]}
        />
      </Card>
    ),
  };
  const shownCards = () =>
    arranged
      .filter((c) => c.visible || customizing)
      .map((c, i) => {
        const title = DASHBOARD_CARDS.find((d) => d.id === c.id)!.title;
        if (!customizing) return <div key={c.id}>{cards[c.id]}</div>;
        return (
          <div key={c.id} className={`dash-card${c.visible ? "" : " dash-card--hidden"}`}>
            <div className="dash-card__tools" role="group" aria-label={`Arrange ${title}`}>
              <strong className="small">{title}</strong>
              <button
                type="button"
                className="btn btn--sm btn--ghost"
                aria-label={`Move ${title} up`}
                disabled={i === 0}
                onClick={() => update((p) => moveCard(p, c.id, -1))}
              >
                ↑
              </button>
              <button
                type="button"
                className="btn btn--sm btn--ghost"
                aria-label={`Move ${title} down`}
                disabled={i === arranged.length - 1}
                onClick={() => update((p) => moveCard(p, c.id, 1))}
              >
                ↓
              </button>
              <button
                type="button"
                className="btn btn--sm btn--ghost"
                onClick={() => update((p) => setCardVisible(p, c.id, !c.visible))}
              >
                {c.visible ? "Hide" : "Show"}
              </button>
            </div>
            {c.visible && cards[c.id]}
          </div>
        );
      });
  return (
    <div className="stack">
      <section className="myspace__hello card">
        <div className="card__body row">
          <Avatar seed={user.avatarSeed} size={56} title={user.displayName} />
          <div>
            <h1 className="page__title">Hi, {user.displayName}</h1>
            <p className="muted">
              {LAYOUT_NAME[layout]} layout{user.isDemo ? " · demo account, shared with other visitors" : ""} ·{" "}
              {s?.followers ?? 0} followers
            </p>
          </div>
          <div className="row row--end" style={{ marginLeft: "auto" }}>
            <Link className="btn btn--primary" to="/workbench">
              New strategy
            </Link>
            <Link className="btn" to="/explore">
              Explore
            </Link>
            <button type="button" className="btn" onClick={() => setImporting((v) => !v)}>
              ↑ Import
            </button>
            <button
              type="button"
              className={`btn btn--ghost${customizing ? " is-active" : ""}`}
              aria-pressed={customizing}
              onClick={() => setCustomizing((v) => !v)}
              title="Choose which cards the Dashboard shows, and their order (also under Settings → Dashboard)"
            >
              {customizing ? "Done" : "Customize"}
            </button>
          </div>
        </div>
      </section>

      {importing && <ImportPanel onClose={() => setImporting(false)} />}
      {customizing && (
        <p className="muted small">
          Move cards up or down, or hide the ones you don't use. Saved with your account — the same choice as
          Settings → Dashboard.
        </p>
      )}

      {shownCards()}
    </div>
  );
}

export function MySpacePage() {
  return (
    <Guard role="starter">
      <MySpaceTabs />
      <MySpaceInner />
    </Guard>
  );
}
